import type { CSSProperties, ReactNode } from 'react';
import React from 'react';
import type { TooltipProps as AntdTooltipProps } from 'antd';
import { Tooltip as AntdTooltip } from 'antd';
import { useIntl } from 'react-intl';
import type { TooltipPlacement } from 'antd/lib/tooltip';
import isString from 'lodash/isString';
import { ClassNames, css, useTheme } from '@emotion/react';
import styled from 'types/theme.types';

export type TooltipProps = {
  title?: string | ReactNode | (() => ReactNode);
  titleId?: string;
  titleValues?: Record<string, any>;
  placement: TooltipPlacement;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  // left undefined by default so existing callers keep antd's hover-only behaviour
  trigger?: AntdTooltipProps['trigger'];
  mouseEnterDelay?: AntdTooltipProps['mouseEnterDelay'];
  children?: ReactNode;
};

// Switch-off keeps passing this exact object, so its DOM is unchanged by construction.
const LEGACY_OVERLAY_STYLE: CSSProperties = { wordBreak: 'break-word', maxWidth: '320px' };

/**
 * `overlayClassName` lands on `.ant-tooltip`, and antd's `:where(...)` selectors carry zero
 * specificity - so of these, only `max-width` needs doubling, being the one antd declares there.
 *
 * The link colour moves here from antd-wrapper's <Global> block. `--text-action` rather than the
 * generated `tooltipLink`, which resolves to border/tint/cyan - a border token on a foreground slot.
 */
const newDesignOverlay = css`
  word-break: break-word;

  && {
    max-width: 320px;
  }

  /* Needs !important because link.tsx sets color: tealBlue !important on every Link, which no amount
     of specificity beats. Without it the overlay link renders tealBlue on the dark spotlight at
     3.9:1, under AA - legacy won the same fight with its own !important. Remove once link.tsx does. */
  .ant-tooltip-inner a {
    color: var(--text-action) !important;
  }
`;

const Tooltip: React.FunctionComponent<TooltipProps> = ({
  title,
  titleId,
  children,
  placement,
  titleValues,
  open,
  onOpenChange,
  trigger,
  mouseEnterDelay
}) => {
  const intl = useIntl();
  // Emotion's theme, not the mobx one: this has to follow the ThemeProvider the app and the specs set.
  const { isNewDesign } = useTheme();

  const antdTitle = titleId ? intl.formatMessage({ id: titleId }, titleValues) : (title ?? '');

  const formattedTitle = antdTitle && isString(antdTitle) ? <StyledTooltip>{antdTitle}</StyledTooltip> : antdTitle;

  return (
    // antd wants a className string, so the class has to be materialised. ClassNames is emotion's way
    // to do that without @emotion/css, which the lint rule bans for needing a CSP `unsafe-inline`.
    <ClassNames>
      {({ css: toClassName }) => (
        <AntdTooltip
          overlayStyle={isNewDesign ? undefined : LEGACY_OVERLAY_STYLE}
          overlayClassName={isNewDesign ? toClassName(newDesignOverlay) : undefined}
          title={formattedTitle}
          placement={placement}
          open={open}
          onOpenChange={onOpenChange}
          mouseEnterDelay={mouseEnterDelay}
          trigger={trigger}>
          {children}
        </AntdTooltip>
      )}
    </ClassNames>
  );
};

export default Tooltip;

const StyledTooltip = styled.div`
  max-height: 360px;
  overflow-y: auto;
  white-space: pre-wrap;
`;
